/**
 * Trust Centre API (policy BUILD-10).
 *
 * The public calls carry no auth header on purpose — /trust must render for a
 * prospect who has never signed in, and sending a stale token would be the only
 * way this page could ever 401.
 *
 * The section flags are the contract. A gated section is ABSENT from the payload
 * rather than present-and-empty, so the optional fields below are genuinely
 * optional and the page must consult `sections` before rendering a heading.
 */
import { API_BASE, getElevatedToken } from '@/api/client'

export interface Subprocessor {
  id: string
  name: string
  purpose: string
  data_categories: string[]
  location: string
  added_at: string
  removed_at: string | null
  notice_sent_at: string | null
  /** True while `added_at` is in the future: announced, not yet in use. */
  pending?: boolean
}

export type TrustIncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved'
export type TrustIncidentSeverity = 'minor' | 'major' | 'critical'

export interface TrustIncident {
  id: string
  title: string
  status: TrustIncidentStatus
  severity: TrustIncidentSeverity
  summary: string
  started_at: string
  resolved_at: string | null
  published_at: string | null
}

export interface SecurityClaim {
  id: string
  category: string
  claim: string
  evidence_kind: 'audit' | 'test' | 'merged_change'
  evidence: string
}

export interface SecurityPracticeGroup {
  category: string
  claims: SecurityClaim[]
}

export interface ResidencyMarket {
  market: string
  market_name: string
  primary_region: string | null
  backup_region: string | null
  cross_border_transfer: string | null
  transfer_mechanism: string | null
  regulator: string | null
  updated_at: string | null
}

export interface PublishedAssessment {
  id: string
  title: string
  period: string
  scope: string
  benchmarks: string[]
  summary: string
  remediation_position: string
}

export interface DisclosurePolicy {
  contact_email: string
  policy_url: string | null
  preferred_languages: string
  commitments: string[]
  safe_harbour: string[]
  out_of_scope: string[]
}

export interface TrustSections {
  status: boolean
  residency: boolean
  assessments: boolean
  dpa_download: boolean
}

export interface TrustOverview {
  sections: TrustSections
  subprocessors: Subprocessor[]
  incidents: TrustIncident[]
  security_practices: SecurityPracticeGroup[]
  disclosure: DisclosurePolicy
  dpa: { url?: string; version?: string | null; request_email: string }
  status_page_url?: string
  residency?: ResidencyMarket[]
  assessments?: PublishedAssessment[]
}

function readAuthToken(): string | null {
  try {
    return localStorage.getItem('fi_token') || localStorage.getItem('sa_token')
  } catch {
    return null
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text()
  const json = (() => {
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return null
    }
  })()

  if (!res.ok) {
    const payload = (json || {}) as { error?: string }
    const error = new Error(payload.error || `Request failed (${res.status})`) as Error &
      Record<string, unknown>
    Object.assign(error, payload, { status: res.status })
    throw error
  }
  return json as T
}

/**
 * API_BASE already includes the `/api` prefix (see client.ts / VITE_API_URL).
 * Paths here must be root-relative to that base — never start with `/api/…`
 * or the browser hits `/api/api/…` and every Trust Centre call 404s (D-15).
 */
function trustUrl(path: string): string {
  const normalised = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${normalised}`
}

/** Public: no Authorization header, by design. */
async function publicRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(trustUrl(path), {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
  })
  return parse<T>(res)
}

/** Platform-admin: carries the bearer token and any active step-up token. */
async function adminRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = readAuthToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const elevated = getElevatedToken()
  if (elevated) headers['X-Elevated-Token'] = elevated

  const res = await fetch(trustUrl(path), {
    ...options,
    headers: { ...headers, ...(options?.headers || {}) },
  })
  return parse<T>(res)
}

// --- Public -----------------------------------------------------------------

export function getTrustOverview(): Promise<TrustOverview> {
  return publicRequest<TrustOverview>('/trust/overview')
}

export function subscribeToSubprocessorNotices(email: string): Promise<{ ok: true; message: string }> {
  return publicRequest('/trust/subprocessors/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export function confirmSubprocessorSubscription(token: string): Promise<{ ok: true }> {
  return publicRequest('/trust/subprocessors/confirm', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

export function unsubscribeFromSubprocessorNotices(id: string, token: string): Promise<{ ok: true }> {
  return publicRequest('/trust/subprocessors/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ id, token }),
  })
}

// --- Platform admin ---------------------------------------------------------

export interface SubprocessorInput {
  name: string
  purpose: string
  location: string
  added_at: string
  data_categories?: string[] | string
  removed_at?: string | null
}

export interface NoticeResult {
  attempted: number
  sent: number
  failed: number
  days_notice: number
  short_notice: boolean
}

export function listSubprocessorsForAdmin(): Promise<{ subprocessors: Subprocessor[] }> {
  return adminRequest('/admin/trust/subprocessors')
}

export function createSubprocessor(
  input: SubprocessorInput,
): Promise<{ subprocessor: Subprocessor; notice: NoticeResult | null }> {
  return adminRequest('/admin/trust/subprocessors', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateSubprocessor(
  id: string,
  patch: Partial<SubprocessorInput>,
): Promise<{ subprocessor: Subprocessor }> {
  return adminRequest(`/admin/trust/subprocessors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function deleteSubprocessor(id: string): Promise<{ ok: true }> {
  return adminRequest(`/admin/trust/subprocessors/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function resendSubprocessorNotice(id: string): Promise<{ notice: NoticeResult }> {
  return adminRequest(`/admin/trust/subprocessors/${encodeURIComponent(id)}/notice`, {
    method: 'POST',
  })
}

export interface AdminSubscriber {
  id: string
  email: string
  status: 'pending' | 'confirmed' | 'unsubscribed'
  confirmed_at: string | null
  unsubscribed_at: string | null
  created_at: string
}

export function listNoticeSubscribers(): Promise<{ subscribers: AdminSubscriber[] }> {
  return adminRequest('/admin/trust/subscribers')
}

export interface IncidentInput {
  title: string
  summary: string
  started_at: string
  status?: TrustIncidentStatus
  severity?: TrustIncidentSeverity
  resolved_at?: string | null
  published_at?: string | null
}

export function listIncidentsForAdmin(): Promise<{ incidents: TrustIncident[] }> {
  return adminRequest('/admin/trust/incidents')
}

export function createIncident(input: IncidentInput): Promise<{ incident: TrustIncident }> {
  return adminRequest('/admin/trust/incidents', { method: 'POST', body: JSON.stringify(input) })
}

export function updateIncident(
  id: string,
  patch: Partial<IncidentInput>,
): Promise<{ incident: TrustIncident }> {
  return adminRequest(`/admin/trust/incidents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function deleteIncident(id: string): Promise<{ ok: true }> {
  return adminRequest(`/admin/trust/incidents/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
